import { decodeHtmlEntities, escapeHtml } from '@utils/htmlEntities';

/**
 * Utility functions for parsing and reconstructing markdown documents
 * split by H2 headings
 */

export type SectionFormat = 'markdown' | 'xml';

export interface ParsedSection {
  id: string;
  title: string;
  text: string;
  startIndex: number;
  endIndex: number;
  contentStartIndex: number;
  contentEndIndex: number;
  format: SectionFormat;
  attributes?: Record<string, string>;
}

/**
 * Slugify a string to match section ID format
 * Converts to lowercase, replaces non-alphanumeric with dashes, removes leading/trailing dashes
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const XML_SECTION_REGEX = /<section\b([^>]*)>([\s\S]*?)<\/section>/gi;

function createDefaultSection(markdown: string): ParsedSection {
  return {
    id: 'document',
    title: 'Document',
    text: markdown,
    startIndex: 0,
    endIndex: markdown.length,
    contentStartIndex: 0,
    contentEndIndex: markdown.length,
    format: 'markdown',
  };
}

export function parseSectionAttributes(attributeString: string): Record<string, string> {
  const attributes: Record<string, string> = {};
  if (!attributeString) {
    return attributes;
  }

  const attrRegex = /([a-zA-Z_:][\w:.-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let match: RegExpExecArray | null;

  while ((match = attrRegex.exec(attributeString)) !== null) {
    const [, key, doubleQuotedValue, singleQuotedValue] = match;
    const value = (doubleQuotedValue ?? singleQuotedValue ?? '').trim();
    attributes[key] = decodeHtmlEntities(value);
  }

  return attributes;
}

function parseXmlSections(markdown: string): ParsedSection[] {
  const sections: ParsedSection[] = [];
  let match: RegExpExecArray | null;
  XML_SECTION_REGEX.lastIndex = 0;

  while ((match = XML_SECTION_REGEX.exec(markdown)) !== null) {
    const [fullMatch, attributeString = '', innerContent = ''] = match;
    const startIndex = match.index ?? 0;
    const endIndex = startIndex + fullMatch.length;

    const openingTagLength = fullMatch.indexOf('>') + 1;
    const closingTagLength = '</section>'.length;
    const contentStartIndex = startIndex + openingTagLength;
    const contentEndIndex = endIndex - closingTagLength;

    const attributes = parseSectionAttributes(attributeString);
    const rawTitle = attributes.title?.trim();
    const title = rawTitle || 'Untitled Section';
    const rawId = attributes.id?.trim();
    const generatedId = slugify(title || `section-${sections.length + 1}`);
    const id = rawId || generatedId || `section-${sections.length + 1}`;

    sections.push({
      id,
      title,
      text: innerContent.trim(),
      startIndex,
      endIndex,
      contentStartIndex,
      contentEndIndex,
      format: 'xml',
      attributes,
    });
  }

  return sections;
}

function getUniqueSectionId(baseId: string, existingIds: Set<string>): string {
  let candidate = baseId;
  let suffix = 1;
  while (existingIds.has(candidate)) {
    candidate = `${baseId}-${suffix}`;
    suffix += 1;
  }
  return candidate;
}

/**
 * Parse markdown into H2-based sections
 */
export function parseMarkdownH2Sections(markdown: string): ParsedSection[] {
  if (!markdown || !markdown.trim()) {
    return [createDefaultSection(markdown)];
  }

  // First, attempt to parse XML-style sections
  const xmlSections = parseXmlSections(markdown);
  if (xmlSections.length > 0) {
    return xmlSections;
  }

  // Split by H2 headings (## )
  const h2Regex = /^##\s+(.+)$/gm;
  const sections: ParsedSection[] = [];
  const matches = Array.from(markdown.matchAll(h2Regex));

  if (matches.length === 0) {
    // No H2 headings, treat entire document as one section
    return [createDefaultSection(markdown)];
  }

  // Process each section
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const title = match[1].trim();
    const headingStartIndex = match.index;
    const contentStartIndex = match.index + match[0].length;
    const contentEndIndex = i < matches.length - 1 ? matches[i + 1].index : markdown.length;
    const text = markdown.substring(contentStartIndex, contentEndIndex).trim();

    // Create slug from title (slugified version of heading text)
    const id = slugify(title);

    sections.push({
      id,
      title,
      text,
      startIndex: headingStartIndex,
      endIndex: contentEndIndex,
      contentStartIndex,
      contentEndIndex,
      format: 'markdown',
    });
  }

  return sections;
}

/**
 * Reconstruct markdown from sections
 * Takes the original markdown and a map of section ID to new content
 */
export function reconstructMarkdown(
  originalMarkdown: string,
  sectionUpdates: Map<string, string>,
): string {
  if (!originalMarkdown || !originalMarkdown.trim()) {
    return originalMarkdown;
  }

  const sections = parseMarkdownH2Sections(originalMarkdown);

  // If no sections or no updates, return original
  if (sections.length === 0 || sectionUpdates.size === 0) {
    return originalMarkdown;
  }

  // Build new markdown by replacing updated sections
  let reconstructed = '';
  let lastIndex = 0;

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const updatedContent = sectionUpdates.get(section.id);
    const originalSectionContent = originalMarkdown.substring(
      section.contentStartIndex,
      section.contentEndIndex,
    );

    if (section.format === 'xml') {
      // Preserve the original opening tag and any spacing before the section content
      if (section.contentStartIndex > lastIndex) {
        reconstructed += originalMarkdown.substring(lastIndex, section.contentStartIndex);
      }

      const sanitizedContent =
        updatedContent !== undefined ? updatedContent.trim() : originalSectionContent;
      reconstructed += sanitizedContent;
      lastIndex = section.contentEndIndex;
      continue;
    }

    // Add content before this section
    if (section.startIndex > lastIndex) {
      reconstructed += originalMarkdown.substring(lastIndex, section.startIndex);
    }

    // Add the H2 heading
    reconstructed += `## ${section.title}\n\n`;

    // Add updated content if available, otherwise use original
    if (updatedContent !== undefined) {
      reconstructed += updatedContent.trim();
    } else {
      reconstructed += section.text.trim();
    }

    // Add newline after section if not at end
    if (i < sections.length - 1) {
      reconstructed += '\n\n';
    }

    lastIndex = section.endIndex;
  }

  // Add any remaining content after the last section
  if (lastIndex < originalMarkdown.length) {
    reconstructed += originalMarkdown.substring(lastIndex);
  }

  return reconstructed;
}

/**
 * Update a specific section in the markdown and return the full reconstructed markdown
 */
export function updateSectionInMarkdown(
  originalMarkdown: string,
  sectionId: string,
  newSectionContent: string,
): string {
  const sectionUpdates = new Map<string, string>();
  sectionUpdates.set(sectionId, newSectionContent);
  return reconstructMarkdown(originalMarkdown, sectionUpdates);
}

/**
 * Update a section's heading in the markdown
 */
export function updateSectionHeading(
  originalMarkdown: string,
  sectionId: string,
  newHeading: string,
): string {
  const sections = parseMarkdownH2Sections(originalMarkdown);

  // Find the section to update
  const sectionIndex = sections.findIndex(s => s.id === sectionId);
  if (sectionIndex === -1) {
    // Section not found, return original
    return originalMarkdown;
  }

  const section = sections[sectionIndex];

  if (section.format === 'xml') {
    const beforeSection = originalMarkdown.substring(0, section.startIndex);
    const afterOpeningTag = originalMarkdown.substring(section.contentStartIndex);
    const attributes = { ...(section.attributes ?? {}) };
    attributes.title = newHeading;

    const attributeString = Object.entries(attributes)
      .map(([key, value]) => `${key}="${escapeHtml(value)}"`)
      .join(' ');
    const newOpeningTag = `<section${attributeString ? ` ${attributeString}` : ''}>`;

    return beforeSection + newOpeningTag + afterOpeningTag;
  }

  // Replace the heading in the markdown
  const headingMatch = originalMarkdown.substring(section.startIndex).match(/^##\s+(.+)$/m);
  if (!headingMatch) {
    return originalMarkdown;
  }

  const headingStartInSection = headingMatch.index ?? 0;
  const headingEndInSection = headingStartInSection + headingMatch[0].length;
  const absoluteHeadingStart = section.startIndex + headingStartInSection;
  const absoluteHeadingEnd = section.startIndex + headingEndInSection;

  // Replace the heading
  const beforeHeading = originalMarkdown.substring(0, absoluteHeadingStart);
  const afterHeading = originalMarkdown.substring(absoluteHeadingEnd);

  return beforeHeading + `## ${newHeading}` + afterHeading;
}

/**
 * Remove a section from the markdown by its section ID
 */
export function removeSectionFromMarkdown(originalMarkdown: string, sectionId: string): string {
  const sections = parseMarkdownH2Sections(originalMarkdown);

  // Find the section to remove
  const sectionIndex = sections.findIndex(s => s.id === sectionId);
  if (sectionIndex === -1) {
    // Section not found, return original
    return originalMarkdown;
  }

  // If it's the only section, return empty string
  if (sections.length === 1) {
    return '';
  }

  // Build new markdown without the removed section
  // We'll reconstruct by copying everything except the section to remove
  const sectionToRemove = sections[sectionIndex];
  const beforeSection = originalMarkdown.substring(0, sectionToRemove.startIndex);
  const afterSection = originalMarkdown.substring(sectionToRemove.endIndex);

  if (sectionToRemove.format === 'xml') {
    return beforeSection + afterSection;
  }

  // Combine before and after, removing any extra newlines
  const reconstructed = (beforeSection.trimEnd() + '\n\n' + afterSection.trimStart()).trim();

  return reconstructed;
}

/**
 * Reorder sections in markdown based on a new order of section IDs
 * Takes the original markdown and an array of section IDs in the desired order
 * Returns the reconstructed markdown with sections in the new order
 */
export function reorderSectionsInMarkdown(
  originalMarkdown: string,
  newSectionOrder: string[],
): string {
  if (!originalMarkdown || !originalMarkdown.trim()) {
    return originalMarkdown;
  }

  const sections = parseMarkdownH2Sections(originalMarkdown);

  // If no sections or order doesn't match, return original
  if (sections.length === 0 || sections.length !== newSectionOrder.length) {
    return originalMarkdown;
  }

  // Check if order actually changed
  const currentOrder = sections.map(s => s.id);
  const orderChanged = currentOrder.some((id, index) => id !== newSectionOrder[index]);
  if (!orderChanged) {
    return originalMarkdown;
  }

  // Create a map of section ID to section for quick lookup
  const sectionMap = new Map(sections.map(s => [s.id, s]));

  const isXmlDocument = sections.every(section => section.format === 'xml');

  // Reconstruct markdown in new order
  let reconstructed = '';

  // Get any content before the first section
  const firstSection = sections[0];
  if (firstSection.startIndex > 0) {
    reconstructed += originalMarkdown.substring(0, firstSection.startIndex);
  }

  // Add sections in new order
  for (let i = 0; i < newSectionOrder.length; i++) {
    const sectionId = newSectionOrder[i];
    const section = sectionMap.get(sectionId);

    if (!section) {
      // Section not found, skip it
      continue;
    }

    // Extract the full section from original markdown (including heading or tags)
    const sectionMarkdown = originalMarkdown.substring(section.startIndex, section.endIndex);

    // Add the section
    reconstructed += isXmlDocument ? sectionMarkdown : sectionMarkdown.trimEnd();

    // Add spacing between sections (except after last one)
    if (!isXmlDocument && i < newSectionOrder.length - 1) {
      reconstructed += '\n\n';
    }
  }

  // Get any content after the last section
  const lastSection = sections[sections.length - 1];
  if (lastSection.endIndex < originalMarkdown.length) {
    reconstructed += originalMarkdown.substring(lastSection.endIndex);
  }

  return reconstructed;
}

/**
 * Insert a new H2 section after the specified section
 * Takes the original markdown, sectionId to insert after, and optional new section title
 * Returns the updated markdown with the new section inserted
 */
export function insertSectionAfter(
  originalMarkdown: string,
  sectionId: string,
  newSectionTitle: string = 'New Note Section',
): string {
  const sections = parseMarkdownH2Sections(originalMarkdown);
  const isXmlDocument = sections.length > 0 && sections.every(section => section.format === 'xml');
  const existingIds = new Set(sections.map(section => section.id));

  // Find the section to insert after
  const sectionIndex = sections.findIndex(s => s.id === sectionId);
  if (sectionIndex === -1) {
    // Section not found, append to end
    const trimmed = originalMarkdown.trim();
    if (!trimmed) {
      if (isXmlDocument) {
        const baseId = slugify(newSectionTitle) || 'section-1';
        const newSectionId = getUniqueSectionId(baseId, existingIds);
        return `<section title="${escapeHtml(newSectionTitle)}" id="${newSectionId}">\n\n</section>\n`;
      }
      return `## ${newSectionTitle}\n\n`;
    }
    if (isXmlDocument) {
      const baseId = slugify(newSectionTitle) || `section-${sections.length + 1}`;
      const newSectionId = getUniqueSectionId(baseId, existingIds);
      return `${trimmed}\n<section title="${escapeHtml(newSectionTitle)}" id="${newSectionId}">\n\n</section>\n`;
    }
    return `${trimmed}\n\n## ${newSectionTitle}\n\n`;
  }

  const section = sections[sectionIndex];

  if (isXmlDocument) {
    const insertIndex = section.endIndex;
    const beforeSection = originalMarkdown.substring(0, insertIndex);
    const afterSection = originalMarkdown.substring(insertIndex);
    const baseId = slugify(newSectionTitle) || `section-${sections.length + 1}`;
    const newSectionId = getUniqueSectionId(baseId, existingIds);
    const newSectionMarkdown = `\n<section title="${escapeHtml(newSectionTitle)}" id="${newSectionId}">\n\n</section>\n`;
    return beforeSection + newSectionMarkdown + afterSection;
  }

  // Insert the new section after this section's endIndex
  // Trim trailing whitespace from beforeSection to prevent accumulation
  const beforeSection = originalMarkdown.substring(0, section.endIndex).trimEnd();
  const afterSection = originalMarkdown.substring(section.endIndex);

  // Build the new section markdown
  const newSectionMarkdown = `\n\n## ${newSectionTitle}\n\n`;

  // Combine: before section + section content + new section + after section
  return beforeSection + newSectionMarkdown + afterSection;
}
